import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Monitor, FileDown, Calendar, Clock, ExternalLink, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { pdf } from '@react-pdf/renderer';
import ServiceOrderPDF from '../components/project/ServiceOrderPDF';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ServiceOrderHeader } from '../components/project/ServiceOrderHeader';
import { ServiceOrderPresentationCard } from '../components/project/ServiceOrderPresentationCard';
import { HoursUtilizationDashboard } from '../components/project/HoursUtilizationDashboard';
import socketService from '../services/socket';
import { useToast } from '../contexts/ToastContext';
import {
  ServiceOrderWithTasks,
  ServiceOrderStatusColors,
  ServiceOrderStatusLabels
} from '../types/serviceOrder';
import {
  Task,
  TaskAreaLabels,
  TaskAreaColors,
  UpdateTaskRequest
} from '../types/task';
import {
  getServiceOrder,
  updateTask,
  exportServiceOrderToJira,
  getProject,
  getJiraSprints
} from '../services/api';
import '../styles/corporate-theme.css';
import config from '../config/environment';

// Helper to convert image URL to base64 via backend proxy (avoids CORS issues)
const imageUrlToBase64 = async (url: string, token: string): Promise<string | null> => {
  try {
    // If already base64, return as-is
    if (url.startsWith('data:')) return url;

    // Use backend proxy to fetch image (config.apiUrl already includes /api)
    const baseUrl = config.apiUrl.replace('/api', '');
    const response = await fetch(
      `${baseUrl}/api/utils/image-proxy?url=${encodeURIComponent(url)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      console.warn('Image proxy failed:', response.status);
      return null;
    }

    const data = await response.json();
    return data.base64 || null;
  } catch (error) {
    console.warn('Failed to convert logo to base64:', error);
    return null;
  }
};

// Convert SVG base64 to PNG base64 capped to a max height (react-pdf Image does not support SVG)
const svgToPngBase64 = (svgDataUri: string, maxH = 56): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      const natW = img.naturalWidth || 200;
      const natH = img.naturalHeight || 200;
      const scale = Math.min(1, maxH / natH);
      const w = Math.round(natW * scale);
      const h = Math.round(natH * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas context unavailable')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load SVG image'));
    img.src = svgDataUri;
  });
};

// Ensure logo base64 is in a format supported by @react-pdf/renderer (PNG or JPEG)
const ensurePdfCompatibleImage = async (base64: string): Promise<string | null> => {
  try {
    if (base64.startsWith('data:image/svg')) {
      return await svgToPngBase64(base64);
    }
    return base64;
  } catch (error) {
    console.warn('Failed to convert image for PDF:', error);
    return null;
  }
};

interface JiraSprint {
  id: number;
  name: string;
  state: 'active' | 'future' | 'closed';
  startDate?: string;
  endDate?: string;
}

const ServiceOrderPresentation: React.FC = () => {
  const { projectId, serviceOrderId } = useParams<{
    projectId: string;
    serviceOrderId: string;
  }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [serviceOrder, setServiceOrder] = useState<ServiceOrderWithTasks | null>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingTask, setUpdatingTask] = useState<string | null>(null);
  const [exportingToJira, setExportingToJira] = useState(false);
  const [sprints, setSprints] = useState<JiraSprint[]>([]);
  const [selectedSprintId, setSelectedSprintId] = useState<number | undefined>(undefined);
  const [loadingSprints, setLoadingSprints] = useState(false);
  const [exportMode, setExportMode] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Load service order data
  const loadData = async () => {
    if (!projectId || !serviceOrderId) return;

    try {
      setLoading(true);
      setError(null);

      // Load both service order and project data
      const [serviceOrderResponse, projectResponse] = await Promise.all([
        getServiceOrder(projectId, serviceOrderId),
        getProject(projectId)
      ]);

      const serviceOrderData = serviceOrderResponse.data.data.serviceOrder;
      const projectData = projectResponse.data.data.project;

      if (!serviceOrderData) {
        setError('Ordem de serviço não encontrada');
        return;
      }

      setServiceOrder(serviceOrderData);
      setProject(projectData);

      // Load sprints if Jira is enabled
      if (projectData?.jiraEnabled) {
        loadSprints();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados');
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load Jira sprints
  const loadSprints = async () => {
    if (!projectId) return;

    try {
      setLoadingSprints(true);
      const response = await getJiraSprints(projectId, false); // Don't include closed sprints by default

      if (response.data.success) {
        const sprintsList: JiraSprint[] = response.data.data.sprints;
        setSprints(sprintsList);

        // Auto-select first active sprint if available
        const activeSprint = sprintsList.find((s: JiraSprint) => s.state === 'active');
        if (activeSprint) {
          setSelectedSprintId(activeSprint.id);
        }
      }
    } catch (err: any) {
      // Don't show error to user, just log it - sprints are optional
      console.error('Erro ao carregar sprints:', err);
    } finally {
      setLoadingSprints(false);
    }
  };

  // Initialize Socket.io and load data
  useEffect(() => {
    loadData();
    
    if (projectId) {
      socketService.connect();
      socketService.joinProject(projectId);

      // Listen for real-time updates
      socketService.on('service-order:updated', handleServiceOrderUpdated);
      socketService.on('task:assigned', handleTaskUpdated);
      socketService.on('task:unassigned', handleTaskUpdated);
      socketService.on('task:updated', handleTaskStatusUpdated);
      socketService.on('hours:updated', handleTaskUpdated);

      return () => {
        socketService.off('service-order:updated', handleServiceOrderUpdated);
        socketService.off('task:assigned', handleTaskUpdated);
        socketService.off('task:unassigned', handleTaskUpdated);
        socketService.off('task:updated', handleTaskStatusUpdated);
        socketService.off('hours:updated', handleTaskUpdated);
        socketService.leaveProject(projectId);
      };
    }
  }, [projectId, serviceOrderId]);

  // Socket event handlers
  const handleServiceOrderUpdated = (data: any) => {
    if (data.projectId === projectId && data.serviceOrder._id === serviceOrderId) {
      loadData();
    }
  };

  const handleTaskUpdated = (data: any) => {
    if (data.projectId === projectId) {
      loadData();
    }
  };

  // Handler específico para atualização de status de task
  const handleTaskStatusUpdated = (data: any) => {
    if (data.projectId !== projectId) return;
    
    setServiceOrder(prevServiceOrder => {
      if (!prevServiceOrder) return prevServiceOrder;
      
      const updatedTask = data.task;
      
      // Atualizar a task no array principal
      const updatedTasks = prevServiceOrder.tasks.map(task => 
        task._id === updatedTask._id ? { ...task, ...updatedTask } : task
      );
      
      // Recalcular tasksByArea
      const updatedTasksByArea = updatedTasks.reduce((acc: any, task) => {
        if (!acc[task.area]) {
          acc[task.area] = {
            tasks: [],
            totalHoras: 0,
            count: 0
          };
        }
        acc[task.area].tasks.push(task);
        acc[task.area].totalHoras += task.horasEstimadas;
        acc[task.area].count++;
        return acc;
      }, {});
      
      // Recalcular totalHoras
      const totalHoras = updatedTasks.reduce((sum, task) => sum + task.horasEstimadas, 0);
      
      return {
        ...prevServiceOrder,
        tasks: updatedTasks,
        tasksByArea: updatedTasksByArea,
        totalHoras,
        totalTasks: updatedTasks.length
      };
    });
    
    // Remove loading state
    setUpdatingTask(null);
  };

  // Task status change handler
  const handleTaskStatusChange = async (taskId: string, newStatus: Task['status']) => {
    if (!projectId) return;

    try {
      setUpdatingTask(taskId);
      const updateData: UpdateTaskRequest = { status: newStatus };
      await updateTask(projectId, taskId, updateData);
      // Não fazer loadData() - aguardar socket event para atualização
    } catch (err: any) {
      showError(err.response?.data?.message || 'Erro ao alterar status da tarefa');
      setUpdatingTask(null); // Remove loading apenas em caso de erro
    }
    // setUpdatingTask(null) será chamado no socket handler
  };

  const handleGoBack = () => {
    navigate(`/projects/${projectId}/service-orders/${serviceOrderId}`);
  };

  // ESC key handler for export mode
  const handleEscKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && exportMode) {
      setExportMode(false);
    }
  }, [exportMode]);

  useEffect(() => {
    if (exportMode) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [exportMode, handleEscKey]);

  const handleEnterExportMode = () => {
    setExportMode(true);
  };

  const handleExitExportMode = () => {
    setExportMode(false);
  };

  const handleExportPDF = async () => {
    if (!serviceOrder || !project) {
      showError('Dados da ordem de serviço não carregados');
      return;
    }

    try {
      setGeneratingPDF(true);

      // Validate required data before generating PDF
      if (!serviceOrder.tasksByArea || Object.keys(serviceOrder.tasksByArea).length === 0) {
        showError('Nenhuma tarefa encontrada na ordem de serviço');
        return;
      }

      // Use logoBase64 if available (pre-converted on upload), otherwise try proxy
      let logoBase64: string | null = null;
      if (project.logoBase64) {
        logoBase64 = project.logoBase64;
      } else if (project.logo) {
        const token = localStorage.getItem('token') || '';
        logoBase64 = await imageUrlToBase64(project.logo, token);
      }

      // Ensure image is in a format supported by react-pdf (PNG/JPEG, not SVG)
      if (logoBase64) {
        logoBase64 = await ensurePdfCompatibleImage(logoBase64);
      }

      // Generate PDF using @react-pdf/renderer
      const pdfDoc = (
        <ServiceOrderPDF
          serviceOrder={serviceOrder}
          projectName={project.name || 'Projeto'}
          projectLogo={logoBase64 || undefined}
        />
      );

      const blob = await pdf(pdfDoc).toBlob();

      // Generate filename: [ProjectName] [OS Name] DD-MM a DD-MM.pdf
      const startDate = new Date(serviceOrder.startDate);
      const endDate = new Date(serviceOrder.endDate);
      const formatDateShort = (date: Date) => {
        if (isNaN(date.getTime())) return 'xx-xx';
        return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      };
      const fileName = `[${project.name || 'Projeto'}] ${serviceOrder.name || 'OS'} ${formatDateShort(startDate)} a ${formatDateShort(endDate)}.pdf`;

      // Download the PDF
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess('PDF exportado com sucesso!');
      setExportMode(false);
    } catch (err: any) {
      console.error('Erro ao gerar PDF:', err);
      // Provide more specific error messages
      let errorMessage = 'Erro ao gerar PDF. ';
      if (err?.message?.includes('font')) {
        errorMessage += 'Falha ao carregar fontes. Verifique sua conexão com a internet.';
      } else if (err?.message?.includes('network') || err?.message?.includes('fetch')) {
        errorMessage += 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (err?.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Tente novamente.';
      }
      showError(errorMessage);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleExportToJira = async () => {
    if (!projectId || !serviceOrderId || !serviceOrder) return;

    try {
      setExportingToJira(true);

      const response = await exportServiceOrderToJira(projectId, serviceOrderId, selectedSprintId);

      if (response.data.success) {
        const { summary } = response.data.data;
        const sprintInfo = selectedSprintId
          ? sprints.find(s => s.id === selectedSprintId)?.name || 'sprint selecionada'
          : 'backlog';

        showSuccess(
          `Exportação concluída: ${summary.success} tarefas exportadas com sucesso para ${sprintInfo}`
        );

        // Reload data to get updated jiraIssueKey fields
        await loadData();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao exportar para JIRA';
      showError(errorMessage);
      console.error('Erro ao exportar para JIRA:', err);
    } finally {
      setExportingToJira(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };


  if (loading) {
    return (
      <div className="corporate-theme min-h-screen flex items-center justify-center">
        <LoadingSpinner message="Carregando apresentação" />
      </div>
    );
  }

  if (error || !serviceOrder) {
    return (
      <div className="corporate-theme min-h-screen flex items-center justify-center">
        <div className="px-6 py-8 max-w-md">
          <div className="mb-6 print:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoBack}
              className="gap-2 hover:bg-white/10 text-corporate-text-primary border-none"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para Gerenciamento
            </Button>
          </div>

          <div className="corporate-card">
            <div className="corporate-card-content text-center">
              <AlertTriangle className="w-12 h-12 text-corporate-error mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-corporate-error mb-2">
                {error || 'Ordem de serviço não encontrada'}
              </h3>
              <Button
                variant="default"
                size="sm"
                onClick={handleGoBack}
                className="print:hidden bg-corporate-brand-primary hover:bg-corporate-brand-dark transition-all duration-200"
              >
                Voltar para Gerenciamento
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`corporate-theme min-h-screen overflow-y-auto ${exportMode ? 'export-mode' : ''}`} style={{ height: '100vh' }}>
      {/* Export Mode Banner */}
      {exportMode && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-corporate-brand-primary/95 backdrop-blur-sm border-b border-corporate-border-primary">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileDown className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Modo de Exportação</span>
              <span className="text-white/70 text-sm">Pressione ESC para sair</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExitExportMode}
                className="gap-2 border-white/30 hover:bg-white/10 text-white"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleExportPDF}
                disabled={generatingPDF}
                className="gap-2 bg-white text-corporate-brand-primary hover:bg-white/90"
              >
                {generatingPDF ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4" />
                )}
                {generatingPDF ? 'Gerando...' : 'Baixar PDF'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={`corporate-content ${exportMode ? 'pt-20' : ''}`} ref={contentRef}>
          {/* Header Actions - Hidden in print and export mode */}
          <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden mb-8 ${exportMode ? 'hidden' : ''}`}>
            {/* Navigation Section */}
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleGoBack}
                className="gap-2 hover:bg-white/10 text-corporate-text-primary border-none transition-all duration-200 hover:scale-105"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Voltar</span>
              </Button>
            </div>

            {/* Action Buttons Section */}
            <div className="flex items-center justify-center sm:justify-end gap-2">
              {/* Sprint Selector - Only show if Jira is enabled and sprints are available */}
              {project?.jiraEnabled && sprints.length > 0 && (
                <div className="flex items-center gap-2">
                  <label htmlFor="sprint-select" className="text-sm text-corporate-text-secondary hidden sm:inline">
                    Sprint:
                  </label>
                  <select
                    id="sprint-select"
                    value={selectedSprintId || ''}
                    onChange={(e) => setSelectedSprintId(e.target.value ? Number(e.target.value) : undefined)}
                    disabled={loadingSprints || exportingToJira}
                    className="px-3 py-1.5 text-sm rounded-md border border-corporate-border-primary bg-corporate-bg-secondary text-corporate-text-primary hover:bg-corporate-bg-hover transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-corporate-brand-primary"
                  >
                    <option value="">Backlog (sem sprint)</option>
                    {sprints.map((sprint) => (
                      <option key={sprint.id} value={sprint.id}>
                        {sprint.name} ({sprint.state === 'active' ? 'Ativa' : sprint.state === 'future' ? 'Futura' : 'Fechada'})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleExportToJira}
                disabled={exportingToJira || !serviceOrder?.tasks?.length}
                className="gap-2 border-corporate-border-primary hover:bg-corporate-bg-hover hover:border-corporate-brand-primary text-corporate-text-primary transition-all duration-200 hover:transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingToJira ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{exportingToJira ? 'Exportando...' : 'Exportar para o Jira'}</span>
                <span className="sm:hidden">JIRA</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={generatingPDF}
                className="gap-2 border-corporate-border-primary hover:bg-corporate-bg-hover hover:border-corporate-brand-primary text-corporate-text-primary transition-all duration-200 hover:transform hover:-translate-y-0.5"
              >
                {generatingPDF ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileDown className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{generatingPDF ? 'Gerando...' : 'Exportar PDF'}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.documentElement.requestFullscreen()}
                className="gap-2 border-corporate-border-primary hover:bg-corporate-bg-hover hover:border-corporate-brand-primary text-corporate-text-primary transition-all duration-200 hover:transform hover:-translate-y-0.5"
              >
                <Monitor className="w-4 h-4" />
                <span className="hidden sm:inline">Apresentar</span>
              </Button>
            </div>
          </div>

          {/* Service Order Header */}
          <ServiceOrderHeader 
            serviceOrder={serviceOrder} 
            projectName={project?.name}
            projectLogo={project?.logo}
          />

          {/* Hours Utilization Dashboard - Hidden in print and export mode */}
          <HoursUtilizationDashboard
            projectId={projectId!}
            billingMonth={serviceOrder.billingMonth}
            billingYear={serviceOrder.billingYear}
            className={`print:hidden ${exportMode ? 'hidden' : ''}`}
          />

          {/* Service Order Content */}
          <ServiceOrderPresentationCard 
            serviceOrder={serviceOrder}
            onTaskStatusChange={handleTaskStatusChange}
            updatingTaskId={updatingTask}
            exportMode={exportMode}
          />

          {/* Footer - Print only */}
          <div className="hidden print:block text-center text-sm text-corporate-text-tertiary pt-8 border-t border-corporate-border-primary">
            <p>Gerado em {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}</p>
          </div>
      </div>
    </div>
  );
};

export default ServiceOrderPresentation;